/** @module csi/slicer/slicer */
define([
	"dojo/_base/declare",
	"dojo/aspect",
	"dojo/Deferred",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/on",
	"dojo/query",
	"dojo/topic",
	"dijit/Destroyable",
	"dijit/form/Button",
	"csi/widget/CheckBox",
	"dijit/TitlePane",
	"dstore/Memory",
	"dgridNew/ColumnSet",
	"dgridNew/OnDemandGrid",
	"dgridNew/extensions/CompoundColumns",
	"dgridNew/extensions/ColumnResizer",
	"dgridNew/Tree",
	"csi/_base/underscore",
	"csi/constants",
	"csi/def",
	"csi/dojox/layout/GridContainerLite",
	"csi/dojox/mdnd/dropMode/OverDropMode",
	"csi/html",
	"csi/locale",
	"csi/types",
	"csi/xml",
	"csi/dgrid/FooterMixin",
	"./slicerCalc",
	"./slicerColumnResizer",
	"./slicerSticky"
], function(declare, aspect, Deferred, domClass, domConstruct, on, query, topic, Destroyable, Button, CheckBox, TitlePane, dMemory, ColumnSet,
	OnDemandGrid, CompoundColumns, ColumnResizer, Tree, _, constants, def, GridContainerLite, OverDropMode, html, locale,
	types, xml, FooterMixin, SlicerCalc, slicerColumnResizer, SlicerSticky) {

	//TODO: Remove time analyzing debugs (console.time() & console.timeEnd())

	//TODO: Slicer may not resize properly if browser window size was changed while not in slicer tab: may be roll back to default horizonatl scroller and footer positining?
	//TODO: Implement HTML5 Drag and Drop
	//TODO: Clean slicerWidget._getClassifierValueStore() from useless variables, data, calculations
	//TODO: Try to put sticky container at the bottom of body instead of inside scrollNode and see if it increase performance.
	//TODO: Get rid of multiple spec definition, multiple calls to spec.format, use colClsNamesStore instead
	//TODO: Optimize total columns algorithms. A lot of array concats probably worth looking.

	//TODO: Format control in Custom Metric Dialog should use standard Centric control (select with our set of formats)
	//TODO: Color HL dialog uses metric's format. If format supposed to hide some digits, and user enters those digits as setting in dlg - next time dlg will not show the value. (#.00% and enter 0.001)

	/** Character to separate parts of path */
	/** Drag and drop types indexes for this.dndTypes */
	const DND_TYPE_CLASSIFIER = 0;
	const DND_TYPE_METRICS = 1;
	/** Constant for very left column field name */
	const FIELD_ROW_CLS = 'rowCls';
	/** Expand mode */
	const EXPAND_DEFAULT = 0; // use CV's expandAll value
	const EXPAND_ALL = 1; // Exapnd All
	const EXPAND_NONE = 2; // Collapse All

	/** if slicer has more than specified number of rows then it will not auto expand and "expand all" option will be ignored */
	const MAX_AUTO_EXPAND_ROWS = 3000;

	/** if operation "expand" take longer than specified time - it will be stopped and part of slicer will be not expanded */
	const MAX_EXPAND_DURATION = 45000;

	function isVar(obj) {
		return (obj != null) && !isNaN(obj);
	}

	/**
	 * Return CSS class for compare column
	 * @param {number} value
	 * @return {string}
	 */
	function addComparisonColor(value) {
		if (!value) { // if value is undefined or zero - use default color
			return '';
		}
		if (value > 0) { // positive values
			return ' csi-slicer-compare-positive';
		} else if (value < 0) { // negative values
			return ' csi-slicer-compare-negative';
		}
	}

	function getColId(node) {
		return node.columnId.substr(node.columnId.lastIndexOf("-") + 1);
	}

	return declare(SlicerCalc, {
		/**
		 * This is data model for Slicer. For more details refer to ./slicerCalc.js module description
		 * @property {Object} colData - {colTree, treeData, columns:[[[treeColumn]],[[compoundColumns]]]}
		 */
		colData: null,
		/** @property {Source} rowsContainer - DnD Source object for row clasisfiers */
		rowsContainer: null,
		/** @property {Source} columnsContainer - DnD Source object for column clasisfiers */
		columnsContainer: null,
		/** @property {number} _columnCount - total number of columns in slicer. Used for optimization and debugging */
		_columnCount: 0,
		/** {Object} _config - slicer configuration object */
		_config: {},
		/** {dgrid} _grid = Slicer grid */
		_grid: null,
		/** {DomNode} _domNode - slicer container node */
		_domNode: null,
		/** Expand Mode - defines tree's default expand mode */
		_expandMode: EXPAND_DEFAULT,
		/** {ContentPane} _slicerGridContainer - slicer container object */
		_slicerGridContainer: null,
		/** {Object} _cfgHandle - config subscription handle (Dojo/Topic subscription handle)*/
		_cfgHandle: null,
		/** {string[]} dndTypes - DND types accepted */
		dndTypes: [_.uniqueId(), _.uniqueId()],
		/** {Memory} store */
		store: null,
		/** {SlicerConfig} _slicerConfig - reference to SlicerConfig object */
		_slicerConfig: null,
		/** {SlicerEditor} _slicerEditor - reference to SlicerEditor object */
		_slicerEditor: null,
		/** {slicerWidet} _slicerWidet - reference to SlicerWidget */
		_slicerWidget: null,
		/** {Object} dndHandle - dojox dnd topic subscription*/
		_dndHandle: null,

		/**
		 * Return Spec for given key
		 * @param {String} id
		 */
		_getSpec: function(id) {
			return this._slicerWidget.getSpec(id);
		},

		/**
		 * Format given value with given spec
		 * @param {Spec} spec
		 * @param {number|string} value
		 * @return {number | string}
		 */
		_specFormatValue: function(spec, value) {
			const {format, ren} = spec;
			return (ren && value) ? ren.formatValue(value, format) : value;
		},

		/**
		 * Common function to render table cell. Sets CSS classes on the cell.
		 * @param {Object} object
		 * @param {string} value
		 * @param {Object} node
		 * @param {Object} options
		 */
		_renderCell: function(object, value, node, options, cfgHl, spec) {
			var valValid = isVar(value);
			const bCompare = options.isCompare;
			const bComparePercent = options.isComparePercent;
			if (!valValid || (value === Infinity)) {
				html.text(node, '');
			} else {
				const format = bComparePercent ? "0.00%" : spec.format;
				var txt = spec.ren.formatValue(value, format);
				html.text(node, txt);
			}
			var cssClass = valValid ? '' : 'csi-bg-not-applicable ';
			cssClass += object.isMetricsRow ? 'csi-slicer-metrics-row' : 'csi-slicer-subtotals';
			if (bCompare && valValid) {
				cssClass += addComparisonColor(value);
			}
			if (options.isTotal && valValid) {
				cssClass += ' bold';
			}

			var _cssClassHl = 'csi-slicer-color-hl-';
			if (valValid && object.isMetricsRow && !options.isCompare && !options.isTotal && cfgHl) {
				var cssClassHl = '';
				if (spec.format && spec.format.indexOf('%') >= 0) {
					value = value * 100;
				}
				_.some(cfgHl, function(rule) {
					switch (rule.condition) {
						case constants.SLICER_HL_LESS:
							if (value < rule.value) {
								cssClassHl = _cssClassHl + rule.color;
								return true;
							}
							break;
						case constants.SLICER_HL_LESS_EQ:
							if (value <= rule.value) {
								cssClassHl = _cssClassHl + rule.color;
								return true;
							}
							break;
						case constants.SLICER_HL_GREATER:
							if (value > rule.value) {
								cssClassHl = _cssClassHl + rule.color;
								return true;
							}
							break;
						case constants.SLICER_HL_GREATER_EQ:
							if (value >= rule.value) {
								cssClassHl = _cssClassHl + rule.color;
								return true;
							}
							break;
						case constants.SLICER_HL_BETWEEN:
							if (value >= rule.value && value <= rule.value2) {
								cssClassHl = _cssClassHl + rule.color;
								return true;
							}
							break;
						case constants.SLICER_HL_OTHER: cssClassHl = _cssClassHl + rule.color;
					}
					return false;
				});
				if (cssClassHl) {
					cssClass += ' ' + cssClassHl;
				}
			}
			domClass.add(node, cssClass);
		},

		/**
		 * Lookup store for value's name. Empty name is valid.
		 * @param {Memory} store
		 * @param {string} value
		 * @return {string}
		 */
		_storeGetName: function(store, value) {
			var storeName = store.getName(value);
			return storeName == undefined ? value : storeName;
		},

		/**
		 * Common function to render table cell. Sets CSS classes on the cell.
		 * @param {Object} object
		 * @param {string} value
		 * @param {Object} node
		 * @param {Object} options
		 * @param {Object} spec
		 * @param {Object} column
		 */
		_renderClassifierCell: function(object, value, node, options, spec, column) {
			if (column && column.isDummy) {
				domClass.add(node, 'csi-bg-not-applicable ');
				return;
			}
			if (!column && object.childs) { // very left tree column and expandable row
				node.title = locale.resource("SlicerDblClickExpand");
			}
			var div = html.create('div', {'class': 'csi-slicer-header-cell'}, node);
			var isCompare = false;
			var isTotal = false;
			var isTotalTop = false;
			var isTotalMetric = false;
			if (value && (value.indexOf(this.DELIMITER_CHAR) >= 0)) {
				var split = value.split(this.DELIMITER_CHAR);
				value = split[1];
				var ending = split[2] || '';
				isCompare = split[0].indexOf(this.COMPARE_COL_SUF) > 0;
				isTotal = split[0].indexOf(this.TOTAL_COL_SUF) >= 0; // word "Total" should be rendered
				isTotalTop = isTotal && (ending == this.TOTAL_COL_SUF); // empty cell should be rendered
				isTotalMetric = isTotal && !!value; // metric is last row and it is here - metic name should be rendered
			}
			var txt = value;
			var a;
			if (isTotal && !isTotalMetric) {
				if (isTotalTop) {
					txt = locale.resource("Total");
				} else {
					txt = '';
				}
			} else if (spec) {
				// Following line with format introduced for enums
				// I am not putting it under condition spec.type == xml.ATTR_ENUM as I am not sure if there another cases like that or not
				// When spec.type == xml.ATTR_REF it will not be called as spec.format is undefined for ref
				txt = this._specFormatValue(spec, value);
				var id = spec.ra.getId();
				if (spec.type == xml.ATTR_REF) {
					a = def.createBrowseAnchorForType(spec.rc.getChildClass(id), value, div);
					txt = this._storeGetName(this.store, txt);
				}
			}
			if (a || isCompare) {
				if (txt == '') {
					txt = locale.getEmptyName();
				}
			}
			//			if (options && isVar(options.field)) txt += ':' + options.field; // EGOR DEBUG, Do not delete for now, very useful for slicer dev /**/
			if (isCompare) {
				txt = locale.resource("SlicerVs") + txt;
			}
			div.title = txt;
			html.text(a || div, txt);

			if (object) {
				var cssClass = object.isMetricsRow ? 'csi-slicer-metrics-row' : 'csi-slicer-subtotals';
				domClass.add(node, cssClass);
			}
			if (options?.isRealColumn) {
				domClass.add(node, 'csi-slicer-realcolumn');
			}
			if (options?.bRowCls) {
				domClass.add(node, 'csi-slicer-rowCls-cell');
				if (txt === undefined) {
					domClass.add(node, 'csi-slicer-rowCls-subtotal');
				}
				if (options.bRowClsLast) {
					domClass.add(node, 'csi-slicer-rowCls-cell-last');
				}
			}
		},

		/**
		 * Render row classifiers header cell (upper left)
		 * @param {DomNode} node
		 * @param {string[]} rowCls - row Classifiers array
		 */
		_renderRowClsHeaderCell: function(node, rowCls, bFlat) {
			var self = this;
			node.title = locale.resource("SlicerDragDrop");

			// Drag and drop container for individual classifiers/metrics
			var classifiersContainer = this.rowsContainer = new GridContainerLite({
				acceptTypes: [this.dndTypes[DND_TYPE_CLASSIFIER]],
				'class': 'csi-slicer-rowcls-pill-container',
				dragMode: OverDropMode.DRAG_MODE_PILE,
				nbZones: 1,
				isAutoOrganized: false
			}).placeAt(node);
			// Add row classifiers pills
			for (var i = 0; i < rowCls.length; i++) {
				var clsObj = this._config.classifierStore.query({id: rowCls[i]});
				var txt = clsObj.length ? clsObj[0].name : rowCls[i];
				var btn = new TitlePane({
					title: txt,
					tooltip: txt,
					'class': "csi-slicer-pill csi-slicer-classifier",
					dndType: this.dndTypes[DND_TYPE_CLASSIFIER],
					open: false,
					toggleable: false
				});
				classifiersContainer.addChild(btn);
				var textNode = query('.dijitTitlePaneTextNode', btn.domNode);
				domClass.add(textNode[0], html.SLICER_CLASSIFIER_ID);
				html.setDataAttribute(textNode[0], html.SLICER_CLASSIFIER_ID, rowCls[i]);
			}
			if (!bFlat) {
				// Expand All / Collapse All
				var cbDiv = html.create('div', {'class': 'csi-slicer-expand-all-div'}, node);
				new Button({
					label: locale.resource("SlicerExpandAll"),
					'class': "csi-link-btn",
					onClick: function() {
						self.expandAll(true);
					}
				}).placeAt(cbDiv);
				html.create('div', {'class': 'dijitToolbarSeparator dijitInline'}, cbDiv);
				new Button({
					label: locale.resource("SlicerCollapseAll"),
					'class': "csi-link-btn",
					onClick: function() {
						self.expandAll(false);
					}
				}).placeAt(cbDiv);
			}
		},

		/**
		 * Render column classifiers header cell (upper central header cell with horizontal column classifier pills)
		 * @param {DomNode} node
		 * @param {string[]} colCls - column Classifiers array
		 */
		_renderCompoundHeaderCell: function(node, colCls) {
			function createMetricsBtn() {
				// Metrics button
				var btn = new Button({
					label: 'Metrics',
					'class': "csi-secondary-btn csi-slicer-pill csi-slicer-metrics",
					dndType: self.dndTypes[DND_TYPE_METRICS]
				});
				var textNode = query('.dijitButtonText', btn.domNode);
				domClass.add(textNode[0], html.SLICER_CLASSIFIER_ID);
				return btn;
			}

			domClass.add(node, 'csi-slicer-colcls-pill-container');
			node.title = locale.resource("SlicerDragDrop");

			// Drag and drop container for individual classifiers/metrics
			var classifiersContainer = this.columnsContainer = new GridContainerLite({
				acceptTypes: [this.dndTypes[DND_TYPE_CLASSIFIER]],
				dragMode: OverDropMode.DRAG_MODE_PILE,
				'class': 'csi-slicer-rowcls-pill-container csi-slicer-float-container',
				nbZones: 1,
				isAutoOrganized: false
			}).placeAt(node);

			var self = this;
			// Column classifiers
			for (var i = 0; i < colCls.length; i++) {
				if (i == this._config.metricsPos) {
					classifiersContainer.addChild(createMetricsBtn());
				}
				var clsObj = this._config.classifierStore.query({id: colCls[i]});
				var txt = clsObj.length ? clsObj[0].name : colCls[i];
				var btn = new Button({
					label: txt,
					title: txt,
					'class': "csi-secondary-btn csi-slicer-pill csi-slicer-classifier",
					dndType: this.dndTypes[DND_TYPE_CLASSIFIER]
				});
				classifiersContainer.addChild(btn);
				var textNode = query('.dijitButtonText', btn.domNode);
				domClass.add(textNode[0], html.SLICER_CLASSIFIER_ID);
				html.setDataAttribute(textNode[0], html.SLICER_CLASSIFIER_ID, colCls[i]);
			}

			if (i <= this._config.metricsPos) {
				classifiersContainer.addChild(createMetricsBtn());
			}
		},

		/**
		 * Function receive metricKey or classifierKey and return it's name.
		 * This function should be used in the situation when we don't know if we have classifier or metric.
		 * @param {string} prop - property key
		 * @return {string} - return resolved name of the key.
		 */
		_resolveKeyName: function(key) {
			var res = this._config.metricStore.get(key);
			return res ? res.name : key;
		},

		/**
		 * Recurring function: creates compound columns object
		 * @param {Object} colTree
		 * @param {array} colCls - list of classifiers, to refer from rendering function
		 * @param {number} lvl - index of currently used classifier, to refer from rendering function
		 * return {Object[]}
		 */
		createCompoundColumns: function(colTree, colCls, lvl) {
			function createColumn(colTree, key, fieldName, cfgHl, metric, self) {
				// Summary: This function createColumn() is only called for the bottom row of compound header
				// So renderCell() function will be called for all slicer cells
				var spec = self._getSpec(metric);
				// COMPARE_COL_SUF is suffix, so it can't be at the beginning, therefore > 0 instead of >= 0 (so we can also add prefix if we will need)
				var split = String(key).split(self.DELIMITER_CHAR);
				var isTotal = (split.length > 1) && split[0].indexOf(self.TOTAL_COL_SUF) >= 0; // word "Total" should be rendered
				var isCompare = (split.length > 1) && split[0].indexOf(self.COMPARE_COL_SUF) > 0;
				var column = {
					label: fieldName,
					field: colTree[key].field,
					isCompare: isCompare,
					isComparePercent: isCompare && self._config.compareTo.bPercent,
					isDummy: colTree[key].isDummy,
					isTotal: isTotal,
					spec: spec, sortable: false,
					renderCell: function(object, value, node, options) { // function called for all slicer cells
						options.isCompare = this.isCompare;
						options.isComparePercent = this.isComparePercent;
						options.isTotal = this.isTotal;
						self._renderCell(object, value, node, options, cfgHl, spec);
					}
				};
				column.renderHeaderCell = function(node) {
					isCompare = this.isCompare;
					var spec = null;
					if (this.isCompare) { // this==column
						spec = self._getSpec(self._config.compareTo.key);
					} else if (self._config.metricsPos < lvl) { // createColumn() is only called for the bottom row of compound header
						spec = self._getSpec(colCls[lvl - 1]);
					}
					var options = {};
					options.isRealColumn = true; // function createColumn is only called for very bottom row of compound columns;
					/*					// This debug info seems to be very useful. Do not delete for now.
										// EGOR DEBUG ------------ drawing column field id for each column
										if (colTree && isVar(colTree[key].field)) {
											options.field = colTree[key].field;
										}
										// -------------------*/
					self._renderClassifierCell(null, this.label, node, options, spec, column);
				};
				return column;
			}
			var self = this;
			var res = [];
			for (var key in colTree) {
				if (!_.isObject(colTree[key])) {
					continue;
				}
				if (lvl == colCls.length) { // Bottom row of compound header
					var metric = colTree[key].metric;
					var fieldName = this._resolveKeyName(key);
					var cfgHl = (self._config.metricsHlVisible[metric]) ? self._config.metricsHl[metric] : null;
					res.push(createColumn(colTree, key, fieldName, cfgHl, metric, self));
				} else {
					var childs = this.createCompoundColumns(colTree[key], colCls, lvl + 1);
					var column = {
						label: this._resolveKeyName(key),
						children: childs,
						isDummy: colTree[key].isDummy,
					};
					column.renderHeaderCell = function(node) {
						var spec = (self._config.metricsPos == lvl) ? null :
							(self._config.metricsPos < lvl) ? self._getSpec(colCls[lvl - 1]) : self._getSpec(colCls[lvl]);
						self._renderClassifierCell(null, this.label, node, null, spec, this);
					};
					res.push(column);
				}
			}
			return res;
		},

		/**
		 * @param {Object} obj
		 * Look inside "obj" object for numeric properties and treat them as metrics.
		 * Return array of metrics to use with dgrid row.
		 * Metrics are always numbers.
		 * Metric property name is equal to dgrid column's 'field' name
		 * -- obj
		 * 1 ---- numeric property 1
		 * 2 ---- numeric property 2
		 * ...
		 * 3 ---- object property 1
		 * 4 ---- object property 2
		 * ...
		 * Lines 1 and 2 will become members of resulting object and deleted from source "obj" object.
		 * @reeturn {Object}
		 */
		_getMetrics: function(obj) {
			var res = {};
			for (var prop in obj) {
				if (typeof obj[prop] === "number") {
					res[prop] = obj[prop];
				}
			}
			return res;
		},

		/**
		 * Parse source object (nested objects format), return resulting object in dgrid format;
		 * Function can be called on tree root or no tree branch
		 * @param {Object} object - we were putting stuff in there and have it sorted, not we are getting it out and use
		 * @param {string[]} rowCls - row classifiers
		 * @return {Object[]}
		 */
		_getChildren: function(object, rowCls) {
			var res = [];
			var _object;
			var lvl = 0;
			if (this._grid && this._grid.slicerSticky) {
				this._grid.slicerSticky.deleteRows(); // Clear sticky rows cache if grid requested new children
			}

			// tree branch
			if (object.childs) {
				_object = object.childs;
				lvl = object.level + 1;
			} else { // tree root
				_object = object;

			}
			// Metrics become members of resulting object (dgrid format)
			// Sub-objects are going under "childs" reference.
			for (var prop in _object) {
				if (typeof _object[prop] === "object") {
					var obj = this._getMetrics(_object[prop]);
					obj.rowCls = prop;
					obj.id = _.uniqueId();
					obj.level = lvl;

					if (lvl >= rowCls.length - 1) {
						obj.isMetricsRow = true; // result have no sub-branches, only leaves
					} else {
						obj.childs = _object[prop];
					}
					res.push(obj);
				}
			}
			return res;
		},

		_getFlatSlicerData: function(object, rowCls, rowClsNames) {
			let res = [];
			let _object;
			let lvl = 0;
			if (!rowClsNames) {
				rowClsNames = [];
			}
			// tree branch
			if (object.childs) {
				_object = object.childs;
				lvl = object.level + 1;
			} else { // tree root
				_object = object;
			}
			// Metrics become members of resulting object (dgrid format)
			// Sub-objects are going under "childs" reference.
			for (let prop in _object) {
				if (typeof _object[prop] === "object") {
					rowClsNames.push(prop);
					let isMetricsRow = false;
					const obj = this._getMetrics(_object[prop]);
					obj.rowCls = prop;
					obj.id = _.uniqueId();
					obj.level = lvl;

					if (lvl >= rowCls.length - 1) {
						obj.isMetricsRow = isMetricsRow = true; // result have no sub-branches, only leaves
					} else {
						obj.childs = _object[prop];
					}

					rowClsNames.forEach((name, idx) => {
						obj["cls" + idx] = name;
					});
					if (!isMetricsRow) {
						res = res.concat(this._getFlatSlicerData(obj, rowCls, [].concat(rowClsNames)));
					}
					rowClsNames.pop();
					if (true) {
						res.push(obj);
					}
				}
			}
			return res;
		},

		/**
		 * Create slicer dgrid store
		 * @param {Object[]} _data - source data for store
		 * @param {string[]}rowCls - row classifiers
		 * @return {dstore/Memory}
		 */
		createTreeStore: function(_data, rowCls) {
			var self = this;
			return new dMemory({
				data: _data,
				getChildren: function(object, options) {
					return new dMemory({data: self._getChildren(object, rowCls)});
				},
				mayHaveChildren: function(object) {
					return !!object.childs;
				}
			});
		},

		/**
		 * Create slicer grid
		 * @param {dstore/Memory} dStore
		 * @param {Object[]} columns
		 * @param {DomNode} domNode
		 * @param {Object} grandTotal
		 * @param {integer} maxExpand - number of levels to be expanded by default in grid tree
		 * @param {Object} row classifiers
		 * @return {Object} - created dgrid
		 */
		createSlicerGrid: function(dStore, columns, domNode, grandTotal, maxExpandLvl, rowCls) {
			var self = this;
			var gridResize = _.throttle(function() {
				self._slicerWidget.forceResize();
			}, 150, {leading: false});
			const slicerCSS = this._config.flatSlicer ? " csi-slicer-flat" : " csi-slicer-tree";
			var grid = new declare([OnDemandGrid, slicerColumnResizer, CompoundColumns, ColumnSet, FooterMixin, Tree, Destroyable])({
				'class': 'csi-slicer-dgrid' + slicerCSS,
				adjustLastColumn: true,
				allowTextSelection: true,
				collection: dStore,
				columnSets: columns,
				columnSizes: self._config.columnSizes,
				maintainOddEven: false,
				enableTreeTransitions: false,
				summary: grandTotal,
				maxExpand: maxExpandLvl,
				minRowsPerPage: Infinity,
				maxRowsPerPage: Infinity,

				// Please refer to node_modules/dgrid/OnDemandList.js to see more dgrid properties and detailed descriptions
				//
				// Setting farOffRemoval to Infinte prevent dgrid from removing rows which are already rendered and inserted into grid
				// By default dgrid will remove rows when user scroll far away from those roads.
				// Disabling this will allow to use simplified version of function _expandWhenInDom() which is much faster
				// This fixes performance issues in Chrome 2020
				farOffRemoval: Infinity,

				expandTakesTooLong: function() {
					const expandTimeStart = this._expandTimeStart;
					const expandElapsed = expandTimeStart ? Date.now() - expandTimeStart : 0;
					return expandElapsed > MAX_EXPAND_DURATION;
				},
				expand: function(target, expand, noTransition) {
					if (!this.expandTakesTooLong()) {
						if (expand == null) {
							gridResize();
						}
						return this.inherited(arguments) || new Deferred().resolve();
					} else {
						return new Deferred().resolve();
					}
				},
				renderSummaryCell: function(item, cell, column) {
					var cssClass = '';
					var value = item[column.field];

					const bFlatSlicer = self._config.flatSlicer;
					if (bFlatSlicer) {
						const colId = getColId(cell);
						if ((column.field === "rowCls") && (colId > 0)) {
							// avoid rendering "Grand Totals" label several times
							value = "";
						}
						// remove border for empty row classifiers "Grand Totals" cell
						if (colId < (rowCls.length - 1)) {
							cssClass += " noborder";
						}
					}

					if (column.isCompare && isVar(value)) {
						cssClass += addComparisonColor(value);
					}
					if (column.isTotal && isVar(value)) {
						cssClass += ' bold';
					}
					var spec = column.spec;
					if ((column.field != FIELD_ROW_CLS) && !isVar(value)) { // field == FIELD_ROW_CLS for very left cell with "Grand Total" title
						value = '';
					}
					// value may be infinity after division to 0, for example compare column against 0 old value and show percentage C8-47010
					if (value === Infinity) {
						value = "";
					} else if (spec && spec.ren) {
						const format = column.isComparePercent ? "0.00%" : spec.format;
						value = spec.ren.formatValue(value, format);
					}
					domClass.add(cell, cssClass);
					html.text(cell, value);
				},
				resize: function() {
					if (grid && grid.slicerSticky) {
						grid.slicerSticky.resize();
					}
					this.inherited(arguments);
				},
				// Determine if tree branch should be expanded by default
				// Initial state - leaves are hidden branches are all visible.
				shouldExpand: function(row, level, previouslyExpanded) {
					var maxExpand = this.maxExpand;
					switch (self._expandMode) {
						case EXPAND_ALL:
							maxExpand = 9999;
							break;
						case EXPAND_NONE:
							maxExpand = 0;
							break;
					}
					return level < maxExpand;
				},

				_expandWhenInDom: function(rowElement, options, dfd) {
					// Simplify and speeed up the method since we never remove rendered rows
					if (!this.expandTakesTooLong()) {
						var expandPromise = this.expand(rowElement, true, true, options.scrollingUp);
						if (dfd) {
							expandPromise.then(function() {
								dfd.resolve();
							});
						}
					} else {
						if (dfd) {
							dfd.resolve();
						}
					}
				}

			}, domConstruct.create('div', null, domNode));
			// adjust grid height on data completion, tree expand, tree collapse
			grid.on('dgrid-refresh-complete', function() {
				self._updateExpandAll();
				self._updateSticky();
				gridResize();
			});
			grid.on('dgrid-columnresize', function(ev) {
				if (ev.width == 'auto') {
					return;
				}
				gridResize();
				self._config.columnSizes[ev.columnId] = ev.width;
				self._slicerConfig.set(self._config, true, true);
			});
			grid.on('.dgrid-content .dgrid-column-0-0-0:dblclick', function(ev) {
				self.toggleRow(ev.target.parentNode);
			});
			// restore grid columns width
			_.each(this._config.columnSizes, function(width, colId, obj) {
				grid.resizeColumnWidth(colId, width);
			});

			// Sticky rows: on scroll
			grid.bodyNode.onscroll = function() {
				if (self._config.stickyRows) {
					self.updateStickyRows();
				}
			};
			// Sticky rows: mark rows
			grid.own(aspect.after(grid, 'renderRow', function(row, args) {
				// Add classes to `row` based on `args[0]` here
				var cssClass = 'csi-slicer-row ' + (args[0].childs ? 'csi-slicer-row-subtotal' : 'csi-slicer-row-metrics');
				domClass.add(row, cssClass);
				html.setDataAttribute(row, 'level', args[0].level);
				return row;
			}));
			return grid;
		},

		/*
		 * Create, update, redraw sticky rows
		 * @param {Object} scrollNode
		 */
		updateStickyRows: function() {
			var grid = this._grid;
			var scrollNode = grid.bodyNode;
			if (!grid.slicerSticky) { // Create Sticky container
				var maxLevel = this._config.rowClassifiers.length - 1;
				grid.slicerSticky = new SlicerSticky(scrollNode, maxLevel, grid);
			}
			var sticky = grid.slicerSticky;
			if (!sticky.rowsDefined()) {
				sticky.setRows($(scrollNode).find('.csi-slicer-row'));
			}
			sticky.scroll(scrollNode.scrollTop, grid._columnSetScrollLefts[1]);
		},


		/**
		 * Create row classifiers tree column (left column in dgrid)
		 * @param {string[]} rowCls - row classifiers
		 */
		createTreeColumn: function(rowCls) {
			// Initially just add left tree column with row classifiers
			// Other columns will be added later
			var self = this;
			return {
				renderExpando: true,
				field: FIELD_ROW_CLS,
				label: rowCls.join('/'),
				expandOn: ".dgrid-expando-icon:click",
				renderHeaderCell: function(node) {
					self._renderRowClsHeaderCell(node, rowCls);
				},
				renderCell: function(object, value, node, options) {
					var spec = self._getSpec(rowCls[object.level]);
					self._renderClassifierCell(object, value, node, options, spec, null);
				},
				resizable: false,
				sortable: false
			};
		},

		createFlatSlicerColumn: function(rowCls) {
			// Initially just add left tree column with row classifiers
			// Other columns will be added later
			var self = this;
			const createColumn = (cls) => {
				return {
					field: FIELD_ROW_CLS,
					label: cls,
					renderHeaderCell: function(node) {
						const colId = getColId(node);
						var clsObj = self._config.classifierStore.query({id: rowCls[colId]});
						var txt = clsObj.length ? clsObj[0].name : rowCls[colId];

						const div = html.create('div', {'class': 'csi-flat-slicer-header-cell'}, node);
						html.text(div, txt);
					},
					renderCell: function(object, value, node, options) {
						const colId = getColId(node);
						var spec = self._getSpec(rowCls[colId]);
						const val = options.bExcel ? value : object["cls" + colId];
						options.bRowCls = true;
						options.bRowClsLast = (colId == (rowCls.length - 1));
						self._renderClassifierCell(object, val, node, options, spec, null);
					},
					resizable: true,
					sortable: false
				};
			};
			const children = [];
			rowCls.forEach(cls => {
				children.push(createColumn(cls));
			})
			return children;
		},

		/**
		 * Callback function, called from SlicerConfig on new config set
		 * @param {Object} widget - instance of slicerWidget
		 */
		_updateProps: function(widget) {
			if (!widget._slicerConfig) {
				return;
			}
			var cfg = widget._slicerConfig.get();
			var self = widget._slicer;
			var expandAllChanged = cfg.expandAll != self._config.expandAll;
			var stickyChanged = cfg.stickyRows != self._config.stickyRows;
			self._config = cfg;
			if (stickyChanged) {
				self._updateSticky();
			}
			if (expandAllChanged) {
				self._updateExpandAll();
			}
		},

		toggleRow: function(rowNode) {
			var grid = this._grid;
			var node = rowNode;
			while (!node.classList.contains('csi-slicer-dgrid') &&
				!node.classList.contains('csi-slicer-row-subtotal') && node.parentNode) {
				node = node.parentNode;
			}
			if (node && node.classList.contains('csi-slicer-row-subtotal')) {
				grid._expandTimeStart = Date.now();
				var bExpand = !node.classList.contains('dgrid-row-expanded');
				grid.expand(node, bExpand, true);
				_.each($(node).next().find('.csi-slicer-row-subtotal'), function(row) {
					grid.expand(row, bExpand, true);
				});
				grid._expandTimeStart = 0;
			}
			grid.resize();
			this._slicerWidget.gridResize();
			this._updateSticky();
		},

		expandAll: function(bExpand) {
			this._expandMode = bExpand ? EXPAND_ALL : EXPAND_NONE;
			html.showWait(true);
			// add delay to allow wait screen to appear
			_.delay(() => {
				var grid = this._grid;
				grid._expandTimeStart = Date.now();
				_.each($(grid.bodyNode).find('.csi-slicer-row-subtotal'), function(row) {
					grid.expand(row, bExpand, true);
				});
				grid._expandTimeStart = 0;
				grid.bodyNode.scrollTop = 0;
				grid.resize();
				this._slicerWidget.gridResize();
				this._updateSticky();
				html.showWait(false);
			}, 150); // 100ms is our current animation duration. Have to wait a bit longer to allow animation to complete.
		},

		_updateExpandAll: function() {
			html.showWait(true);
			// add delay to allow wait screen to appear
			_.delay(() => {
				this._expandMode = EXPAND_DEFAULT;
				var maxLevel = this._config.rowClassifiers.length - 1;
				var grid = this._grid;
				var bExpand = this._config.expandAll;

				grid._expandTimeStart = Date.now();
				const maxExpand = grid.maxExpand = bExpand ? maxLevel - 1 : 0;
				_.each($(grid.bodyNode).find('.csi-slicer-row-subtotal'), function(row) {
					const bExpandRow = row.getAttribute('data-csi-level') < maxExpand;
					grid.expand(row, bExpandRow, true);
				});
				grid._expandTimeStart = 0;
				grid.bodyNode.scrollTop = 0;
				grid.resize();
				this._slicerWidget.gridResize();
				this._updateSticky();
				html.showWait(false);
			}, 150);
		},

		_emptySticky: function() {
			var slicerSticky = this._grid.slicerSticky;
			if (slicerSticky) { // may be null if sticky is not yet created (scrollTop == 0)
				slicerSticky.empty();
				slicerSticky.deleteRows();
			}
		},

		_updateSticky: function() {
			this._emptySticky();
			if (this._config.stickyRows) {
				this.updateStickyRows();
			}
			html.showWait(false);
		},

		/**
		 * Get slicer data, calculate pivot, create column structure.
		 * Column structure depends on slicer configuration and is part of calculations
		 * @param {string[]} rowCls - row classifiers
		 * @param {string[]} colCls - column classifiers
		 * @param {string[]} metrics - metrics
		 */
		calculatePivot: function(rowCls, colCls, metrics) {
			// colOrder is not used yet. Supposed to be used to allow metrics to be on top of column classifiers
			//var colOrder = ['season','brand','targetCost','targetRetail'];

			// dgrid first column - tree column
			const bFlatSlicer = this._config.flatSlicer;
			const treeColumn = this.createTreeColumn(rowCls);

			// Compound columns source data object
			const colData = this.parseCube(metrics, rowCls, colCls);

			// Add other columns to our dgrid
			// call to createCompoundColumns to create compound header with all column classifiers and metrics
			const self = this;
			const children = bFlatSlicer ?
				[{
					label: "Row Classifiers",
					children: this.createFlatSlicerColumn(rowCls),
					renderHeaderCell: function(node) {
						self._renderRowClsHeaderCell(node, rowCls, true);
					},
				}, {
					label: "Column Classifiers",
					children: this.createCompoundColumns(colData.colTree, colCls, 0),
					renderHeaderCell: function(node) {
						self._renderCompoundHeaderCell(node, colCls, rowCls);
					},
				}] :
				this.createCompoundColumns(colData.colTree, colCls, 0);

			const compoundColumns = {
				label: colCls.concat(metrics).join('/'),
				children,
				sortable: false,
				resizable: false,
				renderHeaderCell: function(node) {
					if (!bFlatSlicer) {
						self._renderCompoundHeaderCell(node, colCls, rowCls);
					}
				}
			};
			colData.columns = bFlatSlicer ? [[[compoundColumns]]] : [[[treeColumn]], [[compoundColumns]]];
			// this nested array structure is required by ColumSet mixin
			return colData;
		},

		/**
		 * Set function this._updateProps() as a call back to any configuration changes
		 */
		_subscribeToConfigChanges: function() {
			this._cfgHandle = this._slicerConfig.subscribe(this._updateProps);
		},

		/**
		 * Remove function this._updateProps() as a call back to any configuration changes
		 */
		_unsubscribeFromConfigChanges: function() {
			if (this._cfgHandle) {
				this._slicerConfig.unsubscribe(this._cfgHandle);
				this._cfgHandle = null;
			}
		},

		/**
		 * Hi level function: get data and create slicer grid
		 * @param {string[]} rowCls - row classifiers
		 * @param {string[]} colCls - column classifiers
		 * @param {string[]} metrics - metrics
		 */
		createDgridSlicer: function(rowCls, colCls, metrics) {
			console.time("Slicer data processing");

			var self = this;
			// create pivot data
			var colData = this.calculatePivot(rowCls, colCls, metrics);

			// Calculate grand totals by accessing metrics values
			var grandTotal = this._getMetrics(colData.treeData);
			grandTotal.rowCls = locale.resource('SlicerGrandTotal');

			// prepare grid date
			var _data = this._config.flatSlicer ? this._getFlatSlicerData(colData.treeData, rowCls) : this._getChildren(colData.treeData, rowCls);

			// this parameter define expand level before loading "axpand all" option
			// in previous version it used to be calculated here as:
			// = this._config.expandAll ? rowCls.length : rowCls.length - 2;
			// which is bad, because it may force slicer to render itself fully expanded first, 
			// and then later in function _updateExpandAll load "expand all" option and decide to collapse or not collapse,
			// which might be deadly in case of really tall slicers
			// so just set it to ZERO here, and let "expand all" option work in _updateExpandAll
			var maxExpand = 0;

			console.timeEnd("Slicer data processing");

			console.time("Slicer grid creation");

			if (_data.length || this.bEmpty) {
				// create grid and store
				var store = this.createTreeStore(_data, rowCls);
				this._grid = this.createSlicerGrid(store, colData.columns, this._domNode, grandTotal, maxExpand, rowCls);
				this._grid.startup();

				this.colData = colData;

				var self = this;
				this._grid.own(on(this._domNode, "mousemove", function() {
					self._grid._currentlyTouchedColumnSet = null;
				}));
			}

			console.timeEnd("Slicer grid creation");
		},

		/**
		 * Initial slicer create routine
		 */
		init: function(widget, slicerGridContainer, slicerStore, slicerConfig, slicerEditor) {
			this._domNode = slicerGridContainer.domNode;
			this._slicerWidget = widget;
			this._slicerGridContainer = slicerGridContainer;
			this._slicerConfig = slicerConfig;
			this._slicerEditor = slicerEditor;
			this._grid = null;

			this.store = slicerStore;
			this._subscribeToConfigChanges();
			this._config = this._slicerConfig.get();
			this.createDgridSlicer(this._config.rowClassifiers, this._config.columnClassifiers, this._config.metrics);

			if (!this._grid) {
				return;
			}

			var self = this;
			this._dndHandle = topic.subscribe("/dojox/mdnd/drop", function(node, sourceArea, dropIndex) {
				if (domClass.contains(node, 'csi-slicer-pill')) { // detect slicer grid events (ignore Editor's drag drop)
					self._slicerConfig.recreatePivotConfig(self.rowsContainer, self.columnsContainer, null, '.' + html.SLICER_CLASSIFIER_ID);
				}
			});
			_.delay(function() {
				self._grid.resize();
			}, 1);
		},

		destroy: function() {
			this._unsubscribeFromConfigChanges();
			if (this._dndHandle) {
				this._dndHandle.remove();
			}

			this.inherited(arguments);
		}

	});
});

